import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { ArrowLeft, Phone, Mail, AlertTriangle, MapPin } from "lucide-react";

const dayLabel = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("sq-AL", { weekday: "short", day: "numeric" });
};

export default function SecretaryClass() {
    const { id } = useParams();
    const [data, setData] = useState({ students: [], dates: [] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const refresh = async () => {
            try {
                const r = await api.get(`/secretary/class/${id}`, { params: { days: 7 } });
                setData(r.data);
            } finally { setLoading(false); }
        };
        refresh();
        const t = setInterval(refresh, 8000);
        return () => clearInterval(t);
    }, [id]);

    const callTel = (phone) => phone ? `tel:${phone.replace(/\s/g, "")}` : null;

    return (
        <Layout>
            <Link to="/secretary" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>
            <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Klasa</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
                    {data.students.length > 0 ? "Pamja e detajuar" : "Duke ngarkuar…"}
                </h1>
                <p className="text-slate-600 mt-2 text-sm">7 ditët e fundit • Mungesat me ngjyrë • Numra prindësh për kontakt</p>
            </div>

            {!loading && data.students.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">Klasa nuk ka nxënës.</div>
            ) : (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    {/* Header row */}
                    <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-3 bg-bone/50 border-b border-slate-200 text-[10px] uppercase tracking-widest font-bold text-slate-700">
                        <div className="col-span-4">Nxënësi</div>
                        <div className="col-span-3">Kontakt prindi</div>
                        <div className="col-span-4">Mungesat (7 ditët e fundit)</div>
                        <div className="col-span-1 text-right">Streak</div>
                    </div>
                    <div className="divide-y divide-slate-200">
                        {data.students.map((s) => {
                            const streakColor = s.current_streak_days >= 3 ? "bg-terracotta text-white" : s.current_streak_days >= 1 ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-500";
                            return (
                                <div key={s.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 px-4 py-3 hover:bg-bone/30" data-testid={`sec-class-row-${s.id}`}>
                                    <div className="md:col-span-4 flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold shrink-0 text-xs ${s.absent_today ? "bg-terracotta/10 text-terracotta" : "bg-oxford/10 text-oxford"}`}>
                                            {s.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-slate-900 truncate">{s.name}</div>
                                            <div className="text-[11px] text-slate-500 truncate">
                                                {s.father_name && <span>Babai: {s.father_name}</span>}
                                                {s.address && <span className="ml-2 inline-flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" /> {s.address}</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-span-3 flex flex-col gap-1">
                                        {s.parent_phone ? (
                                            <a href={callTel(s.parent_phone)} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-sage/10 text-sage hover:bg-sage/20 text-xs font-semibold border border-sage/30 w-fit" data-testid={`sec-call-${s.id}`}>
                                                <Phone className="w-3 h-3" /> {s.parent_phone}
                                            </a>
                                        ) : (
                                            <span className="text-[11px] text-slate-400">Pa nr. tel</span>
                                        )}
                                        {s.parent_email && (
                                            <a href={`mailto:${s.parent_email}`} className="inline-flex items-center gap-1 text-[11px] text-oxford hover:underline truncate w-fit max-w-full">
                                                <Mail className="w-3 h-3 shrink-0" /> <span className="truncate">{s.parent_email}</span>
                                            </a>
                                        )}
                                    </div>
                                    <div className="md:col-span-4 flex items-center gap-1" data-testid={`streak-${s.id}`}>
                                        {s.days.map((d) => {
                                            const cls = d.absent
                                                ? d.unexcused ? "bg-terracotta text-white" : "bg-amber-500 text-white"
                                                : "bg-sage/30 text-sage";
                                            return (
                                                <div key={d.date} className={`relative flex-1 h-9 sm:h-10 rounded-md flex flex-col items-center justify-center text-[9px] font-bold ${cls}`} title={`${d.date}${d.absent ? ` — ${d.count} mungesa` : " — Prezent"}`}>
                                                    <div className="leading-none">{dayLabel(d.date).split(" ")[1]}</div>
                                                    <div className="leading-none opacity-80 text-[8px] uppercase">{dayLabel(d.date).split(" ")[0]}</div>
                                                    {d.count > 1 && <span className="absolute top-0.5 right-1 text-[9px]">{d.count}</span>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="md:col-span-1 flex md:justify-end items-center">
                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold ${streakColor}`} data-testid={`streak-badge-${s.id}`}>
                                            {s.current_streak_days >= 3 && <AlertTriangle className="w-3 h-3" />}
                                            {s.current_streak_days}d
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </Layout>
    );
}
